/**
 * AVA_FINDINGS - the AI findings of the AVA Inspect tag whose details panel is open.
 *
 * Shared by the Findings tab (side panel) and the marker layers inside the 360 and 3D viewers,
 * which live in different component trees. The panel opens and closes the tag; markers show while
 * it is open, following the tab's review and category filters.
 */
import aIInspectService from '@/features/Dashboard/service/aIInspect.service';
import { fetchFindings, linkFindingInspection } from './avaSnapsApi';
import { FINDING_DISPLAY, FINDING_FILTER, filterByCategories, filterFindings, findingFromAnomaly } from './avaFindings';

const APPROVAL = Object.freeze({ ACCEPTED: 'Accepted', REJECTED: 'Rejected' });

export const AVA_FINDINGS = {
  namespaced: true,

  state: {
    /** Tag whose panel is open; null when no AVA Inspect panel is open. */
    tagId: null,
    findings: [],
    loading: false,
    error: null,
    filter: FINDING_FILTER.ALL,
    /** Selected category keys ('A' - 'D', 'none'); empty shows every category. */
    categories: [],
    /** Markers or polygons on the 360 view. A viewing preference: kept when another tag opens. */
    display: FINDING_DISPLAY.MARKERS,
    selectedId: null,
    /** Finding whose card pops up on a viewer after its marker was clicked: { id, viewer: '360' | '3d' }. */
    popup: null,
    /** Bumped when a viewer marker is clicked, so the panel switches to the Findings tab. */
    revealRequest: 0,
    /** Finding whose accept / reject is in flight. */
    reviewingId: null,
    /** Finding whose 360 image is being opened. */
    openingId: null,
    /** Finding whose edits are being saved. */
    savingId: null,
    requestId: 0,
  },

  getters: {
    visibleFindings: (state) => filterByCategories(filterFindings(state.findings, state.filter), state.categories),
    selected: (state) => state.findings.find((f) => f.id === state.selectedId) || null,
    isOpen: (state) => !!state.tagId,
  },

  mutations: {
    OPEN_TAG(state, tagId) {
      state.tagId = tagId;
      state.findings = [];
      state.error = null;
      state.selectedId = null;
      state.filter = FINDING_FILTER.ALL;
      state.categories = [];
      state.popup = null;
      state.requestId += 1;
    },
    CLOSE(state) {
      state.tagId = null;
      state.findings = [];
      state.error = null;
      state.selectedId = null;
      state.popup = null;
      state.loading = false;
      state.requestId += 1;
    },
    BEGIN_LOAD(state) {
      state.loading = true;
      state.error = null;
      state.requestId += 1;
    },
    SET_FINDINGS(state, findings) {
      state.findings = Array.isArray(findings) ? findings : [];
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_ERROR(state, message) {
      state.error = message;
    },
    SET_FILTER(state, filter) {
      state.filter = filter;
    },
    SET_CATEGORIES(state, keys) {
      state.categories = Array.isArray(keys) ? keys : [];
    },
    SET_DISPLAY(state, display) {
      state.display = Object.values(FINDING_DISPLAY).includes(display) ? display : FINDING_DISPLAY.MARKERS;
    },
    SET_SELECTED_ID(state, id) {
      state.selectedId = id;
    },
    SET_POPUP(state, popup) {
      state.popup = popup;
    },
    REQUEST_REVEAL(state) {
      state.revealRequest += 1;
    },
    SET_APPROVAL(state, { id, approvalStatus }) {
      state.findings = state.findings.map((f) => (f.id === id ? { ...f, approvalStatus } : f));
    },
    SET_REVIEWING_ID(state, id) {
      state.reviewingId = id;
    },
    SET_OPENING_ID(state, id) {
      state.openingId = id;
    },
    SET_SAVING_ID(state, id) {
      state.savingId = id;
    },
    /** The finding's latest generated report. */
    SET_REPORT_URL(state, { id, reportUrl }) {
      state.findings = state.findings.map((f) => (f.id === id ? { ...f, reportUrl } : f));
    },
    /** Replaces one finding after a save; its views and outline are untouched. */
    SET_FINDING(state, finding) {
      state.findings = state.findings.map((f) => (f.id === finding.id ? finding : f));
    },
    SET_INSPECTION(state, { id, inspection }) {
      state.findings = state.findings.map((finding) => (finding.id === id ? { ...finding, inspection } : finding));
    },
  },

  actions: {
    /** Called by the AVA Inspect details panel when it shows a tag. */
    async OPEN_TAG({ commit, dispatch, state }, tagId) {
      if (state.tagId === tagId) return;
      commit('OPEN_TAG', tagId);
      if (tagId) await dispatch('LOAD');
    },

    CLOSE({ commit }) {
      commit('CLOSE');
    },

    async LOAD({ commit, state, rootState }) {
      if (!state.tagId) return;
      commit('BEGIN_LOAD');
      const { requestId, tagId } = state;
      const isCurrent = () => state.requestId === requestId && state.tagId === tagId;
      try {
        const findings = await fetchFindings(rootState.assetId, tagId);
        if (!isCurrent()) return;
        commit('SET_FINDINGS', findings);
        if (state.selectedId && !findings.some((f) => f.id === state.selectedId)) commit('SET_SELECTED_ID', null);
      } catch (err) {
        if (isCurrent()) commit('SET_ERROR', 'The findings of this tag could not be loaded.');
        throw err;
      } finally {
        if (isCurrent()) commit('SET_LOADING', false);
      }
    },

    /** Selects a finding; `reveal` (a viewer marker was clicked) also brings its card into view. */
    SELECT({ commit, state }, { id, reveal = false }) {
      // A popup card belongs to the selected finding; picking another one closes it.
      if (state.popup && state.popup.id !== id) commit('SET_POPUP', null);
      commit('SET_SELECTED_ID', id);
      if (reveal && id) commit('REQUEST_REVEAL');
    },

    /** A viewer marker was clicked: pop its card up on that viewer and highlight it in the list. */
    OPEN_POPUP({ commit, dispatch }, { id, viewer }) {
      commit('SET_POPUP', id ? { id, viewer } : null);
      return dispatch('SELECT', { id, reveal: true });
    },

    CLOSE_POPUP({ commit }) {
      commit('SET_POPUP', null);
    },

    /**
     * Saves a finding edited in the AI Inspect detail panel (FindingEditDialog). `command` is the full
     * anomaly row with the edits: PUT AnomalyInsights/{id} replaces every field it knows from the body.
     * Goes through AI_INSPECT/UPDATE so the AI Inspect page's rows follow, then patches the finding here
     * so its chips and marker colour change at once.
     */
    async UPDATE({ commit, dispatch, state }, command) {
      const id = command?.id;
      if (!id) return null;
      commit('SET_SAVING_ID', id);
      try {
        const saved = (await dispatch('AI_INSPECT/UPDATE', command, { root: true })) || command;
        const current = state.findings.find((f) => f.id === id);
        if (current) commit('SET_FINDING', findingFromAnomaly(current, saved));
        return saved;
      } finally {
        if (state.savingId === id) commit('SET_SAVING_ID', null);
      }
    },

    /** Links a finding to an existing inspection tag, then moves it to that inspection's group. */
    async LINK_INSPECTION({ commit, state, rootState }, { id, inspectionId }) {
      if (!state.tagId || !id || !inspectionId) return null;
      const inspection = await linkFindingInspection(rootState.assetId, state.tagId, id, inspectionId);
      commit('SET_INSPECTION', { id, inspection });
      return inspection;
    },

    /**
     * Generates a new report of the finding. With `saveToDocuments` it is also filed in the tag's
     * documents ("{finding} Finding Report", "... vN" when taken) and the documents are refreshed so
     * the tag's Documents tab shows it. Remembers the report on the finding, so it can be viewed
     * again without regenerating. Returns { report, document } (document null when not saved).
     */
    async GENERATE_REPORT({ commit, dispatch }, { id, saveToDocuments }) {
      let report;
      let document = null;
      if (saveToDocuments) {
        ({ data: { report, document } } = await aIInspectService.saveReportToAvaInspectDocuments(id));
        dispatch('FETCH_ALL_DOCUMENTS', null, { root: true });
      } else {
        ({ data: report } = await aIInspectService.generateReport(id));
      }
      if (!report?.reportUrl) throw new Error('Report unavailable');
      commit('SET_REPORT_URL', { id, reportUrl: report.reportUrl });
      return { report, document };
    },

    /** Accept or reject: the API applies it to the finding and its views on other scans. */
    async REVIEW({ commit, state }, { id, accept }) {
      commit('SET_REVIEWING_ID', id);
      try {
        if (accept) await aIInspectService.accept(id);
        else await aIInspectService.reject(id);
        commit('SET_APPROVAL', { id, approvalStatus: accept ? APPROVAL.ACCEPTED : APPROVAL.REJECTED });
      } finally {
        if (state.reviewingId === id) commit('SET_REVIEWING_ID', null);
      }
    },
  },
};

export default AVA_FINDINGS;
